import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { CustomVizHook } from 'apps/sigma2-ui/src/components/custom-viz/hooks/custom-viz-hook';
import { z } from 'zod';
import { ZodArray } from 'zod';
import { ZodRawShape } from 'zod';
import { ZodObject } from 'zod';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizNotEnoughDataState } from 'libs/end-to-end/src/custom-viz/custom-viz-not-enough-data-state';

export interface UseCustomVizAutoloadParams<
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
> {
  canAnalyse?: boolean;
  context: Pick<CustomVizHook, 'cancelViz' | 'result' | 'status'>;
  onAnalyze: () => void;
  ResultZodType: T;
}

interface UseCustomVizAutoloadPayloadInitialEmptyState {
  isInitialEmptyState: true;
  readyToDisplayDynamicContent: false;
  hasFailed: false;
  isLoading: false;
  isNotEnoughDataState: false;
  data: null;
}

interface UseCustomVizAutoloadPayloadLoadedSuccessfully<
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
> {
  /**
   * Alias for no error and not loading
   */
  isInitialEmptyState: false;
  readyToDisplayDynamicContent: true;
  hasFailed: false;
  isLoading: false;
  isNotEnoughDataState: false;
  data: T['_output'];
}

interface UseCustomVizAutoloadPayloadLoading {
  isInitialEmptyState: false;
  readyToDisplayDynamicContent: false;
  hasFailed: false;
  isLoading: true;
  isNotEnoughDataState: false;
  data: null;
}

interface UseCustomVizAutoloadPayloadError {
  isInitialEmptyState: false;
  readyToDisplayDynamicContent: false;
  hasFailed: true;
  isLoading: false;
  isNotEnoughDataState: false;
  data: null;
}

interface UseCustomVizAutoloadPayloadNotEnoughData {
  isInitialEmptyState: false;
  readyToDisplayDynamicContent: false;
  hasFailed: true;
  isLoading: false;
  isNotEnoughDataState: true;
  data: null;
}

export type ParseCustomVizPayload<
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
> =
  | UseCustomVizAutoloadPayloadInitialEmptyState
  | UseCustomVizAutoloadPayloadLoadedSuccessfully<T>
  | UseCustomVizAutoloadPayloadLoading
  | UseCustomVizAutoloadPayloadError
  | UseCustomVizAutoloadPayloadNotEnoughData;

export type UseCustomVizAutoloadPayload<
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
> = ParseCustomVizPayload<T> & {
  resetAutoloader: () => void;
};

/**
 * Makes the CustomViz result easier to work with
 */
export const parseCustomVizResult = <
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
>({
  result,
  status,
  ResultZodType,
}: {
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  ResultZodType: T;
}): ParseCustomVizPayload<T> => {
  const parsedResult =
    result.resultType !== 'err'
      ? z
          .union([ResultZodType, CustomVizNotEnoughDataState])
          .nullable()
          .safeParse(result.value)
      : z
          .union([ResultZodType, CustomVizNotEnoughDataState])
          .nullable()
          .safeParse(null);

  const isLoadingStaleAndFreshVersionStill = status === 'loading-viz';
  const isLoadingFreshVersionStill =
    status === 'loading-viz' || status === 'loading-viz-with-stale-result';

  const data = parsedResult.success ? parsedResult.data : null;

  const hasFailed =
    (result.resultType === 'err' && !isLoadingFreshVersionStill) ||
    status === 'fatal-error';

  const readyToDisplayDynamicContent =
    !isLoadingStaleAndFreshVersionStill && !hasFailed && !!data;

  // Doing this conditional and hardcoding booleans and casting for stronger typing in the union
  if (readyToDisplayDynamicContent) {
    if (!data || 'notEnoughData' in data) {
      return {
        isInitialEmptyState: false,
        readyToDisplayDynamicContent: false,
        hasFailed: true, // Not Enough data should default to an error state until all screens account for it.
        isLoading: false,
        isNotEnoughDataState: true,
        data: null,
      };
    }
    return {
      isInitialEmptyState: false,
      readyToDisplayDynamicContent: true,
      hasFailed: false,
      isLoading: false,
      isNotEnoughDataState: false,
      data: data,
    };
  }

  if (hasFailed) {
    return {
      isInitialEmptyState: false,
      readyToDisplayDynamicContent: false,
      hasFailed: true,
      isLoading: false,
      isNotEnoughDataState: false,
      data: null,
    };
  }

  if (isLoadingStaleAndFreshVersionStill) {
    return {
      isInitialEmptyState: false,
      readyToDisplayDynamicContent: false,
      hasFailed: false,
      isLoading: true,
      isNotEnoughDataState: false,
      data: null,
    };
  }

  return {
    isInitialEmptyState: true,
    readyToDisplayDynamicContent: false,
    hasFailed: false,
    isLoading: false,
    isNotEnoughDataState: false,
    data: null,
  };
};

/**
 * This helps you load your customm viz data on initial page load easily and keep your UI
 * more declarative when handling loading states and knowing when the data is ready or if you need to display an error
 *
 * See the InsightDetailsPage for example usage.
 */
export const useCustomVizAutoload = <
  T extends ZodObject<ZodRawShape> | ZodArray<ZodObject<ZodRawShape>>,
>({
  canAnalyse,
  onAnalyze,
  context,
  ResultZodType,
}: UseCustomVizAutoloadParams<T>): UseCustomVizAutoloadPayload<T> => {
  const { cancelViz, result, status } = context;

  const hasAutoloaded = useRef(false);

  const triggerAnalyze = useCallback(() => {
    if (!hasAutoloaded.current) {
      if (canAnalyse === undefined || canAnalyse) {
        hasAutoloaded.current = true;
        onAnalyze();
      }
    }
  }, [canAnalyse, onAnalyze]);

  const resetAutoloader = useCallback(() => {
    cancelViz();
    hasAutoloaded.current = false;
  }, [cancelViz]);

  useEffect(() => {
    triggerAnalyze();
  }, [triggerAnalyze]);

  return useMemo(() => {
    return {
      ...parseCustomVizResult({
        result,
        status,
        ResultZodType,
      }),
      resetAutoloader,
    };
  }, [result, status, ResultZodType, resetAutoloader]);
};
